import {
  type CSSProperties,
  ForwardedRef,
  forwardRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  adjustLabelTimingsForIntro,
  buildFrameLabelCSS,
  buildFrameLabelTimings,
} from "../core/labels";
import {
  normalizeFrames,
  extractValue,
  extractItemConfig,
} from "../core/normalize";
import { calculateTotalDuration } from "../core/durations";
import {
  applySequentialIntro,
  applySequentialTransitions,
  buildKeyframeTimeline,
  computeZeroBaselineY,
  signedExtentToRect,
  TimelineEntry,
} from "../core/timelines";
import { isLightColor } from "../core/color";
import {
  FRAME_LABEL_VERTICAL_SPACE,
  KEY_LABEL_VERTICAL_SPACE,
  defaultHold,
  defaultOptions,
} from "../core/constants";
import { Frame } from "../types";
import { useAnimator, AnimatorHandle } from "../hooks/useAnimator";

let idCounter = 0;

const EASING_PRESETS: Record<string, [number, number, number, number]> = {
  ease: [0.25, 0.1, 0.25, 1],
  "ease-in": [0.42, 0, 1, 1],
  "ease-out": [0, 0, 0.58, 1],
  "ease-in-out": [0.42, 0, 0.58, 1],
};

const createCubicBezier = (
  p1x: number,
  p1y: number,
  p2x: number,
  p2y: number,
) => {
  if (p1x === p1y && p2x === p2y) {
    return (t: number) => t;
  }

  const cx = 3 * p1x;
  const bx = 3 * (p2x - p1x) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * p1y;
  const by = 3 * (p2y - p1y) - cy;
  const ay = 1 - cy - by;

  const sampleCurveX = (t: number) => ((ax * t + bx) * t + cx) * t;
  const sampleCurveY = (t: number) => ((ay * t + by) * t + cy) * t;
  const sampleCurveDerivativeX = (t: number) => (3 * ax * t + 2 * bx) * t + cx;

  const solveCurveX = (x: number) => {
    let t = x;
    for (let i = 0; i < 8; i += 1) {
      const x2 = sampleCurveX(t) - x;
      if (Math.abs(x2) < 1e-6) return t;
      const d2 = sampleCurveDerivativeX(t);
      if (Math.abs(d2) < 1e-6) break;
      t -= x2 / d2;
    }

    let t0 = 0;
    let t1 = 1;
    t = x;
    while (t0 < t1) {
      const x2 = sampleCurveX(t);
      if (Math.abs(x2 - x) < 1e-6) return t;
      if (x > x2) {
        t0 = t;
      } else {
        t1 = t;
      }
      t = (t0 + t1) / 2;
    }
    return t;
  };

  return (x: number) => sampleCurveY(solveCurveX(x));
};

const countDecimals = (value: number) => {
  if (!Number.isFinite(value)) return 0;
  const text = Math.abs(value).toString();
  if (!text.includes("e")) {
    const [, fraction = ""] = text.split(".");
    return fraction.length;
  }

  const [coeff, exp] = text.split("e");
  const exponent = Number.parseInt(exp, 10);
  const fractionLength = (coeff.split(".")[1] || "").length;
  if (Number.isNaN(exponent)) return fractionLength;
  return Math.max(0, fractionLength - exponent);
};

const ABBREVIATIONS = [
  { value: 1e12, suffix: "T" },
  { value: 1e9, suffix: "B" },
  { value: 1e6, suffix: "M" },
  { value: 1e3, suffix: "k" },
];

const roundToSignificant = (value: number, digits: number) => {
  if (!Number.isFinite(value)) return 0;
  if (value === 0) return 0;
  const abs = Math.abs(value);
  const scale = 10 ** (digits - 1 - Math.floor(Math.log10(abs)));
  return Math.round(value * scale) / scale;
};

const resolveEasing = (easing: string) => {
  const normalized = easing?.trim() || "linear";
  if (normalized === "linear") {
    return (t: number) => t;
  }

  const preset = EASING_PRESETS[normalized];
  if (preset) {
    return createCubicBezier(...preset);
  }

  const match = normalized.match(/cubic-bezier\(([^)]+)\)/i);
  if (match) {
    const parts = match[1]
      .split(",")
      .map((value) => Number.parseFloat(value.trim()));
    if (parts.length === 4 && parts.every((value) => Number.isFinite(value))) {
      return createCubicBezier(parts[0], parts[1], parts[2], parts[3]);
    }
  }

  return (t: number) => t;
};

const buildValueTimeline = (
  values: number[],
  holdStart: number,
  holdEnd: number,
  duration: number,
  advanceDelay: number,
): TimelineEntry[] => {
  const frameCount = values.length;
  if (frameCount === 0) return [];

  const safeValues = values.map((value) =>
    Number.isFinite(value) ? value : 0,
  );
  const entries: TimelineEntry[] = [];
  const startHold = Math.max(0, Math.min(holdStart, 100));
  const endHold = Math.max(0, Math.min(holdEnd, 100 - startHold));
  const startHoldMs = (startHold / 100) * duration;
  const endHoldMs = (endHold / 100) * duration;
  const animatedDuration = Math.max(0, duration - startHoldMs - endHoldMs);
  const stepTime = frameCount > 1 ? animatedDuration / (frameCount - 1) : 0;
  const pause = Math.max(0, advanceDelay);
  const totalDuration = calculateTotalDuration(
    duration,
    advanceDelay,
    frameCount,
  );
  const toPercent = (timeMs: number) => (timeMs / totalDuration) * 100;

  const firstValue = safeValues[0];
  entries.push({ percent: 0, height: firstValue });
  if (startHoldMs > 0) {
    entries.push({ percent: toPercent(startHoldMs), height: firstValue });
  }

  let currentTime = startHoldMs;
  for (let index = 1; index < frameCount; index += 1) {
    const prevValue = safeValues[index - 1];
    if (pause > 0) {
      currentTime += pause;
      entries.push({ percent: toPercent(currentTime), height: prevValue });
    }
    currentTime += stepTime;
    entries.push({
      percent: toPercent(currentTime),
      height: safeValues[index],
    });
  }

  const lastValue = safeValues[frameCount - 1];
  if (endHoldMs > 0) {
    const endHoldStart = totalDuration - endHoldMs;
    entries.push({ percent: toPercent(endHoldStart), height: lastValue });
    entries.push({ percent: 100, height: lastValue });
  }

  if (!entries.some((entry) => entry.percent === 0)) {
    entries.unshift({ percent: 0, height: firstValue });
  }
  if (!entries.some((entry) => entry.percent === 100)) {
    entries.push({ percent: 100, height: lastValue });
  }

  return entries.filter(
    (entry, index, arr) =>
      index === 0 || entry.percent !== arr[index - 1].percent,
  );
};

const interpolateTimeline = (
  timeline: TimelineEntry[],
  percent: number,
  easingFn: (t: number) => number,
) => {
  if (!timeline.length) return 0;

  const clamped = Math.max(0, Math.min(100, percent));
  if (clamped <= timeline[0].percent) return timeline[0].height;

  for (let i = 1; i < timeline.length; i += 1) {
    const prev = timeline[i - 1];
    const next = timeline[i];
    if (clamped <= next.percent) {
      const span = next.percent - prev.percent;
      if (span <= 0) return next.height;
      const local = (clamped - prev.percent) / span;
      const eased = easingFn(local);
      return prev.height + (next.height - prev.height) * eased;
    }
  }

  return timeline[timeline.length - 1].height;
};

const getIterationProgress = (
  elapsedMs: number,
  delayMs: number,
  iterationDuration: number,
  iterations: number | "infinite",
  loopMode: "normal" | "alternate",
) => {
  if (elapsedMs < delayMs) return 0;
  const localElapsed = elapsedMs - delayMs;
  if (iterationDuration <= 0) return 1;

  const totalIterations =
    typeof iterations === "number" ? Math.max(0, iterations) : Infinity;
  const totalMs = iterationDuration * totalIterations;
  if (Number.isFinite(totalMs) && localElapsed >= totalMs) {
    const finalIteration = Math.max(0, totalIterations - 1);
    const isReverse = loopMode === "alternate" && finalIteration % 2 === 1;
    return isReverse ? 0 : 1;
  }

  const iterationIndex = Math.floor(localElapsed / iterationDuration);
  let progress = (localElapsed % iterationDuration) / iterationDuration;
  if (loopMode === "alternate" && iterationIndex % 2 === 1) {
    progress = 1 - progress;
  }
  return progress;
};

const formatValueLabel = (
  value: number,
  finalValue: number,
  precision = 3,
  prefix = "",
  unitSuffix = "",
) => {
  if (!Number.isFinite(value)) return "--";

  const cappedDecimals = countDecimals(finalValue);
  const absValue = Math.abs(value);
  let suffix = "";
  let scaled = value;

  for (const abbrev of ABBREVIATIONS) {
    if (absValue >= abbrev.value) {
      suffix = abbrev.suffix;
      scaled = value / abbrev.value;
      break;
    }
  }

  let rounded = roundToSignificant(scaled, precision);
  const absRounded = Math.abs(rounded);

  if (absRounded >= 1000 && suffix) {
    const currentIndex = ABBREVIATIONS.findIndex(
      (item) => item.suffix === suffix,
    );
    if (currentIndex > 0) {
      const next = ABBREVIATIONS[currentIndex - 1];
      suffix = next.suffix;
      rounded = roundToSignificant(value / next.value, precision);
    }
  }

  const digits = absRounded >= 1 ? Math.floor(Math.log10(absRounded)) + 1 : 0;
  const requiredDecimals =
    absRounded >= 1 ? Math.max(0, precision - digits) : precision;
  // Only cap decimals by input precision when NOT abbreviated — abbreviation
  // creates new fractional digits (e.g. 1379 → 1.38k) that should be shown.
  const decimals = suffix
    ? requiredDecimals
    : Math.min(requiredDecimals, cappedDecimals);
  const formatted = Math.abs(rounded).toFixed(decimals);
  const sign = rounded < 0 ? "-" : "";
  // `suffix` is the magnitude abbreviation (k/M/…); `unitSuffix` is the
  // author-supplied unit (e.g. "%"), appended last. `prefix` (e.g. "$") leads.
  return `${sign}${prefix}${formatted}${suffix}${unitSuffix}`;
};

// useLayoutEffect warns during SSR; fall back to useEffect when there is no DOM.
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

// Truncate an x-axis label to the number of characters that fit in its bar slot,
// appending an ellipsis. The full text is preserved in a <title> for hover/a11y.
const truncateLabel = (label: string, maxChars: number) => {
  if (!Number.isFinite(maxChars) || maxChars >= label.length) return label;
  if (maxChars <= 1) return `${label.slice(0, 1)}…`;
  return `${label.slice(0, maxChars - 1).trimEnd()}…`;
};

export interface BarChartAnimatorProps {
  frames: Frame[];
  /**
   * Chart width in pixels. Omit to make the chart responsive: it fills its
   * container width (measured via ResizeObserver) and lays out bars/labels at
   * that native pixel width. Provide a number for a fixed-size chart.
   */
  width?: number;
  height?: number;
  padding?: number;
  duration?: number;
  advanceDelay?: number;
  easing?: string;
  loopMode?: "normal" | "alternate";
  iterations?: number | "infinite";
  stagger?: number;
  labels?: boolean;
  labelFontSize?: number;
  valueLabels?: boolean;
  valueLabelPrecision?: number;
  /** Prepended to every value label, e.g. "$". Default "". */
  valueLabelPrefix?: string;
  /**
   * Appended to every value label, e.g. "%". Default "". Intended for
   * non-abbreviated units — values ≥ 1000 still abbreviate (so a "%" suffix on
   * large values would read "1.5k%"; use only where values are in unit range).
   */
  valueLabelSuffix?: string;
  /**
   * Bar ordering. 'none' (default) follows `Object.keys()` of the frame
   * (numeric-like keys first in ascending numeric order, then other keys in
   * insertion order) — not a stable order, and not preserved across JSON
   * round-trips that drop object key order. 'valueDesc' / 'valueAsc' order by
   * final value, so they are stable regardless of key order.
   */
  sort?: "none" | "valueDesc" | "valueAsc";
  animateEach?: boolean;
  hold?: { start?: number; end?: number };
  color?: string;
  dataItemStyle?: "striped" | "solid";
  /**
   * Maximum width in pixels for an individual bar. When bars would exceed this
   * width (common with few series in a wide container), they are clamped and
   * the bar group is centered within the available space.
   */
  maxBarWidth?: number;
  className?: string;
  style?: CSSProperties;
  autoPlay?: boolean;
  /**
   * When false, render the chart immediately in its final state — full bars and
   * final value labels — with no grow-in, count-up, looping, or sequential
   * intro. Distinct from `autoPlay={false}`, which pauses at the *first* frame
   * (empty bars); this pins the *last* frame. The narrative player sets this
   * under `prefers-reduced-motion` (and for backward-nav / PDF export, where the
   * chart should not re-animate). Default true.
   */
  animate?: boolean;
  introPercentOverride?: number;
}

export const BarChartAnimator = forwardRef(function BarChartAnimator(
  props: BarChartAnimatorProps,
  ref: ForwardedRef<AnimatorHandle>,
) {
  // Responsive when the caller omits an explicit width: the chart fills its
  // container instead of being a fixed pixel size.
  const isResponsive = props.width === undefined;

  const settings = useMemo(
    () => ({
      ...defaultOptions,
      ...props,
      hold: { ...defaultOptions.hold, ...(props.hold || {}) },
    }),
    [props],
  );

  const {
    frames,
    width,
    height,
    padding,
    duration,
    advanceDelay,
    easing,
    loopMode,
    iterations,
    stagger,
    labels,
    labelFontSize: rawLabelFontSize,
    valueLabels,
    valueLabelPrecision,
    valueLabelPrefix = "",
    valueLabelSuffix = "",
    sort = "none",
    animateEach,
    hold,
    color = "#ffffff",
    dataItemStyle = "solid",
    className,
    style,
    autoPlay = true,
    animate = true,
    introPercentOverride,
    maxBarWidth,
  } = settings;
  const labelFontSize = Number.isFinite(rawLabelFontSize)
    ? rawLabelFontSize
    : defaultOptions.labelFontSize;
  const valueLabelFontSize = labelFontSize;
  const valueLabelOffset = Math.max(6, Math.round(valueLabelFontSize * 0.65));

  const holdStart = hold?.start ?? defaultHold.start;
  const holdEnd = hold?.end ?? defaultHold.end;

  const animationIdRef = useRef(`ds-bar-${++idCounter}`);
  const baseName = animationIdRef.current;
  const { paused } = useAnimator(ref, autoPlay);
  const valueLabelRefs = useRef<(SVGTextElement | null)[]>([]);
  const firstBarRef = useRef<SVGRectElement | null>(null);

  // Measure the container width in responsive mode and lay out at that pixel
  // width so bars and labels render at native scale (no SVG downscaling).
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [measuredWidth, setMeasuredWidth] = useState<number | null>(null);
  useIsomorphicLayoutEffect(() => {
    if (!isResponsive) return;
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => {
      const next = el.clientWidth;
      if (next > 0) {
        setMeasuredWidth((prev) => (prev === next ? prev : next));
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isResponsive]);

  const effectiveWidth = isResponsive ? (measuredWidth ?? width) : width;

  // In responsive mode the first render has no measurement yet. The placeholder
  // that waits for one is returned below, after every hook has run — returning
  // here would call fewer hooks on the first render than on the measured render
  // that follows, which React rejects ("rendered more hooks than during the
  // previous render").
  const awaitingMeasurement = isResponsive && !effectiveWidth;

  if (!Array.isArray(frames) || frames.length === 0) {
    throw new Error("frames must be a non-empty array");
  }

  const {
    keys: rawKeys,
    globalMin,
    globalMax,
  } = useMemo(() => normalizeFrames(frames), [frames]);
  // Bar order. Default 'none' uses normalizeFrames' key order, which is just
  // Object.keys() (numeric-like keys first in numeric order, then insertion
  // order) — not stable, and not preserved across JSON round-trips like Postgres
  // JSONB. valueDesc/valueAsc instead derive order from the final frame's value,
  // so the order is stable regardless of key order.
  const keys = useMemo(() => {
    if (sort !== "valueDesc" && sort !== "valueAsc") return rawKeys;
    const lastFrame = frames[frames.length - 1] ?? {};
    const finalOf = (k: string) => extractValue(lastFrame[k]) || 0;
    const dir = sort === "valueDesc" ? -1 : 1;
    return [...rawKeys].sort((a, b) => {
      const delta = (finalOf(a) - finalOf(b)) * dir;
      if (delta !== 0) return delta;
      // Equal values would otherwise keep rawKeys order, which is Object.keys()
      // order — the non-deterministic thing this sort exists to escape. Break
      // ties on the key name so tied bars land in the same place every time.
      // Compared by code unit rather than locale so the order can't shift with
      // the runtime's collation. Always ascending: a name is not a value, so it
      // shouldn't flip with valueDesc/valueAsc.
      return a < b ? -1 : a > b ? 1 : 0;
    });
  }, [rawKeys, frames, sort]);
  const valuesByKey = useMemo(() => {
    return keys.reduce<Record<string, number[]>>((acc, key) => {
      acc[key] = frames.map((frame) => extractValue(frame[key]));
      return acc;
    }, {});
  }, [frames, keys]);

  // Extract per-item configurations for each key across all frames
  const itemConfigsByKey = useMemo(() => {
    return keys.reduce<
      Record<string, Array<ReturnType<typeof extractItemConfig>>>
    >((acc, key) => {
      acc[key] = frames.map((frame) => extractItemConfig(frame[key]));
      return acc;
    }, {});
  }, [frames, keys]);

  // Calculate value label styles per item (accounting for per-item colors)
  const valueLabelStylesByKey = useMemo(() => {
    return keys.reduce<
      Record<string, { fill: string; stroke: string; isLight: boolean }>
    >((acc, key) => {
      // Use the first frame's item config to determine color, or fallback to global
      const itemConfig = itemConfigsByKey[key]?.[0];
      const itemColor = itemConfig?.color || color;
      const isLight = isLightColor(itemColor);
      acc[key] = {
        fill: isLight ? "#111" : "#fff",
        stroke: isLight ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.35)",
        isLight,
      };
      return acc;
    }, {});
  }, [keys, itemConfigsByKey, color]);

  const labelTimings = useMemo(
    () =>
      buildFrameLabelTimings(
        frames,
        holdStart,
        holdEnd,
        duration,
        advanceDelay,
      ),
    [frames, holdEnd, holdStart, duration, advanceDelay],
  );
  const frameLabelSpace =
    labelTimings.length > 0
      ? Math.max(FRAME_LABEL_VERTICAL_SPACE, Math.round(labelFontSize * 2))
      : 0;
  const keyLabelSpace = labels
    ? Math.max(KEY_LABEL_VERTICAL_SPACE, Math.round(labelFontSize + 8))
    : 0;
  const chartHeight = Math.max(0, height - frameLabelSpace - keyLabelSpace);
  const keyLabelY = chartHeight + labelFontSize;
  // Y pixel of the zero baseline. Bars grow up from here for positive values
  // and down for negative ones. For all-positive data this is the bottom edge
  // (identical to the previous bottom-anchored behavior).
  const zeroY = useMemo(
    () => computeZeroBaselineY(globalMin, globalMax, chartHeight, padding),
    [globalMin, globalMax, chartHeight, padding],
  );
  const hasNegativeValues = globalMin < 0;

  const introPercent = useMemo(() => {
    if (typeof introPercentOverride === "number") return introPercentOverride;
    if (animateEach && keys.length > 0) {
      return Math.min(40, Math.max(12, keys.length * 24));
    }
    return 0;
  }, [animateEach, introPercentOverride, keys.length]);

  const easingFn = useMemo(() => resolveEasing(easing), [easing]);

  const barTimelines = useMemo(() => {
    return keys.map((key, index) => {
      let timeline = buildKeyframeTimeline(
        valuesByKey[key],
        globalMin,
        globalMax,
        chartHeight,
        padding,
        holdStart,
        holdEnd,
        duration,
        advanceDelay,
      );

      if (introPercent > 0) {
        timeline = applySequentialIntro(
          timeline,
          index,
          keys.length,
          introPercent,
        );
      }
      if (animateEach) {
        timeline = applySequentialTransitions(
          timeline,
          index,
          keys.length,
          introPercent > 0 ? introPercent : 0,
        );
      }

      return timeline;
    });
  }, [
    advanceDelay,
    animateEach,
    chartHeight,
    duration,
    globalMax,
    globalMin,
    holdEnd,
    holdStart,
    introPercent,
    keys,
    padding,
    valuesByKey,
  ]);

  const valueTimelines = useMemo(() => {
    if (!valueLabels) return [];
    return keys.map((key, index) => {
      let timeline = buildValueTimeline(
        valuesByKey[key],
        holdStart,
        holdEnd,
        duration,
        advanceDelay,
      );

      if (introPercent > 0) {
        timeline = applySequentialIntro(
          timeline,
          index,
          keys.length,
          introPercent,
        );
      }
      if (animateEach) {
        timeline = applySequentialTransitions(
          timeline,
          index,
          keys.length,
          introPercent > 0 ? introPercent : 0,
        );
      }

      return timeline;
    });
  }, [
    advanceDelay,
    animateEach,
    duration,
    holdEnd,
    holdStart,
    introPercent,
    keys,
    valueLabels,
    valuesByKey,
  ]);
  const finalValues = useMemo(
    () =>
      keys.map((key) => {
        const values = valuesByKey[key] ?? [];
        const lastValue = values[values.length - 1];
        return Number.isFinite(lastValue) ? lastValue : 0;
      }),
    [keys, valuesByKey],
  );

  const baseTotalDuration = calculateTotalDuration(
    duration,
    advanceDelay,
    frames.length,
  );
  const totalDuration =
    introPercent > 0
      ? Math.round(baseTotalDuration / ((100 - introPercent) / 100))
      : baseTotalDuration;
  const effectiveIterations =
    typeof iterations === "number" ? iterations : "infinite";

  const barKeyframes = useMemo(() => {
    return barTimelines
      .map((timeline, index) => {
        const css = timeline
          .map(({ percent, height: signedExtent }) => {
            const { y, height: rectHeight } = signedExtentToRect(
              signedExtent,
              zeroY,
            );
            return `${percent.toFixed(2)}% { height: ${rectHeight.toFixed(2)}px; y: ${y.toFixed(2)}px; }`;
          })
          .join("\n");
        return `@keyframes ${baseName}-${index} {\n${css}\n}`;
      })
      .join("\n");
  }, [barTimelines, baseName, zeroY]);

  const effectiveLabelTimings =
    introPercent > 0
      ? adjustLabelTimingsForIntro(labelTimings, introPercent)
      : labelTimings;
  const labelCSS = useMemo(
    () => buildFrameLabelCSS(baseName, effectiveLabelTimings),
    [baseName, effectiveLabelTimings],
  );

  const availableWidth = effectiveWidth - padding * 2;
  const gap = keys.length > 0 ? (availableWidth * 0.1) / (keys.length + 1) : 0;
  const rawBarWidth =
    keys.length > 0
      ? (availableWidth - gap * (keys.length + 1)) / keys.length
      : availableWidth;
  const barWidth = maxBarWidth
    ? Math.min(rawBarWidth, maxBarWidth)
    : rawBarWidth;
  // When bars are clamped, center the bar group within the available space.
  const usedWidth = barWidth * keys.length + gap * (keys.length + 1);
  const barGroupOffset = (availableWidth - usedWidth) / 2;
  const maxDelay = Math.max(0, stagger * (keys.length - 1));

  useEffect(() => {
    // Nothing to track until the chart has a width: no bars are mounted, so
    // firstBarRef is null, elapsed pins at 0 and every label ref is missing.
    // The loop would re-arm itself every frame doing no work.
    if (awaitingMeasurement) return;
    // Static mode (animate=false) renders final value labels directly in JSX —
    // no rAF loop needed (and none wanted: nothing is animating to track).
    if (!valueLabels || !animate) return;
    if (paused) {
      return;
    }

    let rafId = 0;
    const step = () => {
      // Get elapsed time from the first bar's CSS animation to stay in sync
      const bar = firstBarRef.current;
      let elapsed = 0;

      if (bar) {
        const animations = bar.getAnimations();
        const barAnimation = animations.find((a) => a instanceof CSSAnimation);
        if (barAnimation && barAnimation.currentTime != null) {
          elapsed = barAnimation.currentTime as number;
        }
      }

      for (let index = 0; index < keys.length; index += 1) {
        const label = valueLabelRefs.current[index];
        if (!label) continue;

        // `elapsed` is read off the first bar, whose animation-delay is 0. Bar
        // `index` starts stagger*index later, so its own progress is the shared
        // clock minus its delay — which is exactly what getIterationProgress
        // computes from (elapsed, delay). Pre-adding the delay here cancelled it
        // back out, leaving every label tracking the first bar: with a non-zero
        // stagger the labels ran ahead of the bars they annotate, and none of
        // them held at zero while their bar was still waiting to start.
        const progress = getIterationProgress(
          elapsed,
          stagger * index,
          totalDuration,
          effectiveIterations,
          loopMode,
        );
        const percent = progress * 100;
        const signedExtent = interpolateTimeline(
          barTimelines[index] ?? [],
          percent,
          easingFn,
        );
        const value = interpolateTimeline(
          valueTimelines[index] ?? [],
          percent,
          easingFn,
        );
        const visibleHeight = Math.abs(signedExtent);
        const inset = Math.max(
          1,
          Math.min(
            valueLabelOffset,
            Math.max(1, visibleHeight - valueLabelFontSize),
          ),
        );
        // Label sits just inside the bar's outer end: near the top for positive
        // bars (above zero), near the bottom for negative bars (below zero).
        const y =
          signedExtent >= 0
            ? zeroY - visibleHeight + inset
            : zeroY + visibleHeight - inset - valueLabelFontSize;
        const isZero = Math.abs(value) < 1e-6;

        if (isZero) {
          if (label.textContent) {
            label.textContent = "";
          }
          if (label.getAttribute("opacity") !== "0") {
            label.setAttribute("opacity", "0");
          }
        } else {
          const text = formatValueLabel(
            value,
            finalValues[index] ?? value,
            valueLabelPrecision,
            valueLabelPrefix,
            valueLabelSuffix,
          );
          if (label.textContent !== text) {
            label.textContent = text;
          }
          if (label.getAttribute("opacity") !== "1") {
            label.setAttribute("opacity", "1");
          }
        }
        label.setAttribute("y", y.toFixed(2));
      }

      if (typeof effectiveIterations === "number") {
        const totalMs =
          totalDuration * Math.max(0, effectiveIterations) + maxDelay;
        if (elapsed >= totalMs) return;
      }

      rafId = requestAnimationFrame(step);
    };

    rafId = requestAnimationFrame(step);

    return () => cancelAnimationFrame(rafId);
  }, [
    barTimelines,
    zeroY,
    easingFn,
    effectiveIterations,
    keys.length,
    loopMode,
    maxDelay,
    paused,
    stagger,
    totalDuration,
    valueLabelFontSize,
    valueLabelOffset,
    valueLabels,
    valueLabelPrecision,
    valueLabelPrefix,
    valueLabelSuffix,
    finalValues,
    valueTimelines,
    animate,
    awaitingMeasurement,
  ]);

  // Responsive mode: hold the placeholder until the ResizeObserver reports a
  // width. Every hook above has already run, so this render and the measured
  // one that follows call hooks in the same order.
  if (awaitingMeasurement) {
    return <div ref={containerRef} style={{ width: "100%", height }} />;
  }

  const chart = (
    <svg
      width={effectiveWidth}
      height={height}
      viewBox={`0 0 ${effectiveWidth} ${height}`}
      role="img"
      aria-label="Animated bar chart"
      className={className}
      style={{
        overflow: "visible",
        color,
        ...(isResponsive ? { display: "block" } : {}),
        ...style,
      }}
    >
      <defs>
        {/* Create striped patterns for each unique color and dataItemStyle */}
        {keys.map((key, index) => {
          const itemConfig = itemConfigsByKey[key]?.[0];
          const itemStyle = itemConfig?.dataItemStyle || dataItemStyle;
          const itemColor = itemConfig?.color || color;

          if (itemStyle === "striped") {
            return (
              <pattern
                key={`${key}-stripe`}
                id={`${baseName}-stripe-${index}`}
                patternUnits="userSpaceOnUse"
                width="32"
                height="32"
                patternTransform="rotate(45)"
              >
                <rect width="16" height="32" fill={itemColor} />
                <rect
                  x="16"
                  width="16"
                  height="32"
                  fill={itemColor}
                  opacity="0.75"
                />
              </pattern>
            );
          }
          return null;
        })}
      </defs>
      {/* Static mode emits no @keyframes — nothing references them. */}
      <style>{animate ? `${barKeyframes}\n${labelCSS}` : ""}</style>
      <g transform={`translate(0, ${frameLabelSpace})`}>
        {hasNegativeValues ? (
          <line
            x1={padding.toFixed(2)}
            x2={(effectiveWidth - padding).toFixed(2)}
            y1={zeroY.toFixed(2)}
            y2={zeroY.toFixed(2)}
            stroke="currentColor"
            strokeOpacity={0.35}
            strokeWidth={1}
          />
        ) : null}
        <g transform={`translate(${padding + barGroupOffset}, 0)`}>
          {keys.map((key, index) => {
            const timeline = barTimelines[index] ?? [];
            const firstExtent = timeline[0]?.height ?? 0;
            const finalExtent =
              timeline[timeline.length - 1]?.height ?? firstExtent;
            // Static mode pins the bar at its final extent; animated mode starts
            // at the first frame and grows via the CSS keyframes. signedExtentToRect
            // maps the (possibly negative) extent to a zero-baseline rect.
            const renderExtent = animate ? firstExtent : finalExtent;
            const { y, height: renderHeight } = signedExtentToRect(
              renderExtent,
              zeroY,
            );
            const x = gap * (index + 1) + barWidth * index;
            const delay = stagger * index;

            // Get per-item configuration (use first frame's config)
            const itemConfig = itemConfigsByKey[key]?.[0];
            const itemStyle = itemConfig?.dataItemStyle || dataItemStyle;
            const itemColor = itemConfig?.color || color;
            const fillValue =
              itemStyle === "striped"
                ? `url(#${baseName}-stripe-${index})`
                : itemColor;

            return (
              <rect
                key={key}
                ref={index === 0 ? firstBarRef : undefined}
                x={x.toFixed(2)}
                width={barWidth.toFixed(2)}
                rx={Math.min(4, barWidth / 3).toFixed(2)}
                fill={fillValue}
                height={renderHeight.toFixed(2)}
                y={y.toFixed(2)}
                style={
                  animate
                    ? {
                        animationName: `${baseName}-${index}`,
                        animationDuration: `${totalDuration}ms`,
                        animationTimingFunction: easing,
                        animationDelay: `${delay}ms`,
                        animationIterationCount: effectiveIterations,
                        animationDirection: loopMode,
                        animationFillMode: "forwards",
                        animationPlayState: paused ? "paused" : "running",
                      }
                    : undefined
                }
              />
            );
          })}
          {valueLabels ? (
            <g>
              {keys.map((key, index) => {
                const x = gap * (index + 1) + barWidth * index + barWidth / 2;
                const timeline = barTimelines[index] ?? [];
                const firstExtent = timeline[0]?.height ?? 0;
                const finalExtent =
                  timeline[timeline.length - 1]?.height ?? firstExtent;
                // Static mode anchors the label to the final bar; animated mode
                // starts at the first frame and the rAF loop drives it from there.
                const renderExtent = animate ? firstExtent : finalExtent;
                const visibleHeight = Math.abs(renderExtent);
                const inset = Math.max(
                  1,
                  Math.min(
                    valueLabelOffset,
                    Math.max(1, visibleHeight - valueLabelFontSize),
                  ),
                );
                const y =
                  renderExtent >= 0
                    ? zeroY - visibleHeight + inset
                    : zeroY + visibleHeight - inset - valueLabelFontSize;
                const initialValue =
                  valueTimelines[index]?.[0]?.height ??
                  valuesByKey[key]?.[0] ??
                  0;
                const finalValue = finalValues[index] ?? initialValue;
                const renderValue = animate ? initialValue : finalValue;
                const renderHidden = Math.abs(renderValue) < 1e-6;

                // Get per-item valueLabel configuration and styles
                const itemConfig = itemConfigsByKey[key]?.[0];
                const showValueLabel = itemConfig?.valueLabels ?? valueLabels;
                const labelStyles = valueLabelStylesByKey[key];

                if (!showValueLabel) return null;

                return (
                  <text
                    key={`${key}-value`}
                    ref={(node) => {
                      valueLabelRefs.current[index] = node;
                    }}
                    x={x.toFixed(2)}
                    y={y.toFixed(2)}
                    textAnchor="middle"
                    fontSize={valueLabelFontSize}
                    fontWeight={600}
                    fill={labelStyles.fill}
                    stroke={labelStyles.stroke}
                    strokeWidth={2}
                    paintOrder="stroke"
                    dominantBaseline="hanging"
                    opacity={renderHidden ? 0 : 1}
                  >
                    {renderHidden
                      ? ""
                      : formatValueLabel(
                          renderValue,
                          finalValue,
                          valueLabelPrecision,
                          valueLabelPrefix,
                          valueLabelSuffix,
                        )}
                  </text>
                );
              })}
            </g>
          ) : null}
        </g>
        {labels ? (
          <g transform={`translate(${padding + barGroupOffset}, 0)`}>
            {keys.map((key, index) => {
              const x = gap * (index + 1) + barWidth * index + barWidth / 2;
              const y = keyLabelY;
              // Fit the label to its slot (bar + one gap) and ellipsis-truncate
              // the overflow. ~0.6em average glyph width for a sans-serif face.
              const slot = barWidth + gap;
              const maxChars = Math.floor(
                (slot * 0.95) / (labelFontSize * 0.6),
              );
              const display = truncateLabel(key, maxChars);
              return (
                <text
                  key={`${key}-label`}
                  x={x.toFixed(2)}
                  y={y.toFixed(2)}
                  textAnchor="middle"
                  fontSize={labelFontSize}
                  fill="currentColor"
                >
                  {display !== key ? <title>{key}</title> : null}
                  {display}
                </text>
              );
            })}
          </g>
        ) : null}
      </g>

      {animate
        ? effectiveLabelTimings.map(({ label }, index) => (
            <text
              key={`frame-label-${index}`}
              x={padding.toFixed(2)}
              y={(padding + labelFontSize).toFixed(2)}
              textAnchor="start"
              fontSize={labelFontSize}
              fontWeight={700}
              fill="currentColor"
              style={{
                animationName: `${baseName}-label-${index}`,
                animationDuration: `${totalDuration}ms`,
                animationTimingFunction: "steps(1, end)",
                animationDelay: "0ms",
                animationIterationCount: effectiveIterations,
                animationDirection: loopMode,
                animationFillMode: "both",
                animationPlayState: paused ? "paused" : "running",
              }}
            >
              {label}
            </text>
          ))
        : /* Static mode: show only the final frame's label, no cycling. */
          effectiveLabelTimings.slice(-1).map(({ label }) => (
            <text
              key="frame-label-final"
              x={padding.toFixed(2)}
              y={(padding + labelFontSize).toFixed(2)}
              textAnchor="start"
              fontSize={labelFontSize}
              fontWeight={700}
              fill="currentColor"
            >
              {label}
            </text>
          ))}
    </svg>
  );

  if (!isResponsive) return chart;

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      {chart}
    </div>
  );
});

export default BarChartAnimator;
